import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Slider,
  Rating,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  addMentalHealthAssessment,
  updateMentalHealthAssessment,
  deleteMentalHealthAssessment,
} from '../features/patients/patientSlice';

const MentalHealthAssessment = ({ patientId }) => {
  const dispatch = useDispatch();
  const { mentalHealth, loading, error } = useSelector(
    (state) => state.patients
  );
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [formData, setFormData] = useState({
    type: 'initial',
    date: new Date().toISOString().split('T')[0],
    mood: 3,
    anxiety: 3,
    sleep: 3,
    energy: 3,
    concentration: 3,
    appetite: 3,
    suicidalIdeation: false,
    suicidalDetails: '',
    selfHarm: false,
    selfHarmDetails: '',
    hallucinations: false,
    hallucinationDetails: '',
    delusions: false,
    delusionDetails: '',
    substanceUse: false,
    substanceDetails: '',
    riskLevel: 'low',
    notes: '',
    recommendations: '',
    followUpDate: '',
  });

  useEffect(() => {
    if (patientId) {
      // Fetch mental health assessments for the patient
      // This would be handled by the patient slice
    }
  }, [patientId]);

  const handleOpenDialog = (assessment = null) => {
    if (assessment) {
      setEditingAssessment(assessment);
      setFormData({
        ...assessment,
        date: assessment.date.split('T')[0],
      });
    } else {
      setEditingAssessment(null);
      setFormData({
        type: 'initial',
        date: new Date().toISOString().split('T')[0],
        mood: 3,
        anxiety: 3,
        sleep: 3,
        energy: 3,
        concentration: 3,
        appetite: 3,
        suicidalIdeation: false,
        suicidalDetails: '',
        selfHarm: false,
        selfHarmDetails: '',
        hallucinations: false,
        hallucinationDetails: '',
        delusions: false,
        delusionDetails: '',
        substanceUse: false,
        substanceDetails: '',
        riskLevel: 'low',
        notes: '',
        recommendations: '',
        followUpDate: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAssessment(null);
    setFormData({
      type: 'initial',
      date: new Date().toISOString().split('T')[0],
      mood: 3,
      anxiety: 3,
      sleep: 3,
      energy: 3,
      concentration: 3,
      appetite: 3,
      suicidalIdeation: false,
      suicidalDetails: '',
      selfHarm: false,
      selfHarmDetails: '',
      hallucinations: false,
      hallucinationDetails: '',
      delusions: false,
      delusionDetails: '',
      substanceUse: false,
      substanceDetails: '',
      riskLevel: 'low',
      notes: '',
      recommendations: '',
      followUpDate: '',
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingAssessment) {
        await dispatch(
          updateMentalHealthAssessment({
            patientId,
            assessmentId: editingAssessment.id,
            assessmentData: formData,
          })
        ).unwrap();
      } else {
        await dispatch(
          addMentalHealthAssessment({
            patientId,
            assessmentData: formData,
          })
        ).unwrap();
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save mental health assessment:', error);
    }
  };

  const handleDelete = async (assessmentId) => {
    if (window.confirm('Are you sure you want to delete this assessment?')) {
      try {
        await dispatch(
          deleteMentalHealthAssessment({
            patientId,
            assessmentId,
          })
        ).unwrap();
      } catch (error) {
        console.error('Failed to delete mental health assessment:', error);
      }
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'low':
        return 'success';
      case 'medium':
        return 'warning';
      case 'high':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant='h5'>Mental Health Assessments</Typography>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          New Assessment
        </Button>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <List>
          {mentalHealth.assessments.map((assessment) => (
            <ListItem key={assessment.id} divider>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant='subtitle1'>
                      {new Date(assessment.date).toLocaleDateString()}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      ({assessment.type})
                    </Typography>
                    <Chip
                      label={`Risk Level: ${assessment.riskLevel}`}
                      color={getRiskLevelColor(assessment.riskLevel)}
                      size='small'
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant='body2'>
                      Mood: {assessment.mood}/5
                    </Typography>
                    <Typography variant='body2'>
                      Anxiety: {assessment.anxiety}/5
                    </Typography>
                    <Typography variant='body2'>
                      Sleep: {assessment.sleep}/5
                    </Typography>
                    <Typography variant='body2'>
                      Energy: {assessment.energy}/5
                    </Typography>
                    <Typography variant='body2'>
                      Concentration: {assessment.concentration}/5
                    </Typography>
                    <Typography variant='body2'>
                      Appetite: {assessment.appetite}/5
                    </Typography>
                    {assessment.notes && (
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mt: 1 }}
                      >
                        Notes: {assessment.notes}
                      </Typography>
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge='end'
                  aria-label='edit'
                  onClick={() => handleOpenDialog(assessment)}
                  sx={{ mr: 1 }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  edge='end'
                  aria-label='delete'
                  onClick={() => handleDelete(assessment.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          {editingAssessment
            ? 'Edit Mental Health Assessment'
            : 'New Mental Health Assessment'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Assessment Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  label='Assessment Type'
                >
                  <MenuItem value='initial'>Initial Assessment</MenuItem>
                  <MenuItem value='followup'>Follow-up Assessment</MenuItem>
                  <MenuItem value='crisis'>Crisis Assessment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='date'
                label='Assessment Date'
                value={formData.date}
                onChange={(e) =>
                  setFormData({ ...formData, date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant='subtitle1' gutterBottom>
                Symptom Ratings
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography gutterBottom>Mood</Typography>
              <Rating
                value={formData.mood}
                onChange={(_, value) =>
                  setFormData({ ...formData, mood: value })
                }
                max={5}
                size='large'
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography gutterBottom>Anxiety</Typography>
              <Rating
                value={formData.anxiety}
                onChange={(_, value) =>
                  setFormData({ ...formData, anxiety: value })
                }
                max={5}
                size='large'
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography gutterBottom>Sleep</Typography>
              <Rating
                value={formData.sleep}
                onChange={(_, value) =>
                  setFormData({ ...formData, sleep: value })
                }
                max={5}
                size='large'
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography gutterBottom>Energy</Typography>
              <Rating
                value={formData.energy}
                onChange={(_, value) =>
                  setFormData({ ...formData, energy: value })
                }
                max={5}
                size='large'
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography gutterBottom>Concentration</Typography>
              <Rating
                value={formData.concentration}
                onChange={(_, value) =>
                  setFormData({ ...formData, concentration: value })
                }
                max={5}
                size='large'
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography gutterBottom>Appetite</Typography>
              <Rating
                value={formData.appetite}
                onChange={(_, value) =>
                  setFormData({ ...formData, appetite: value })
                }
                max={5}
                size='large'
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant='subtitle1' gutterBottom>
                Risk Assessment
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.suicidalIdeation}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        suicidalIdeation: e.target.checked,
                      })
                    }
                  />
                }
                label='Suicidal Ideation'
              />
            </Grid>
            {formData.suicidalIdeation && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label='Suicidal Ideation Details'
                  value={formData.suicidalDetails}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      suicidalDetails: e.target.value,
                    })
                  }
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.selfHarm}
                    onChange={(e) =>
                      setFormData({ ...formData, selfHarm: e.target.checked })
                    }
                  />
                }
                label='Self-Harm'
              />
            </Grid>
            {formData.selfHarm && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label='Self-Harm Details'
                  value={formData.selfHarmDetails}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      selfHarmDetails: e.target.value,
                    })
                  }
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.hallucinations}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        hallucinations: e.target.checked,
                      })
                    }
                  />
                }
                label='Hallucinations'
              />
            </Grid>
            {formData.hallucinations && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label='Hallucination Details'
                  value={formData.hallucinationDetails}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      hallucinationDetails: e.target.value,
                    })
                  }
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.delusions}
                    onChange={(e) =>
                      setFormData({ ...formData, delusions: e.target.checked })
                    }
                  />
                }
                label='Delusions'
              />
            </Grid>
            {formData.delusions && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label='Delusion Details'
                  value={formData.delusionDetails}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      delusionDetails: e.target.value,
                    })
                  }
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.substanceUse}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        substanceUse: e.target.checked,
                      })
                    }
                  />
                }
                label='Substance Use'
              />
            </Grid>
            {formData.substanceUse && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label='Substance Use Details'
                  value={formData.substanceDetails}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      substanceDetails: e.target.value,
                    })
                  }
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Risk Level</InputLabel>
                <Select
                  value={formData.riskLevel}
                  onChange={(e) =>
                    setFormData({ ...formData, riskLevel: e.target.value })
                  }
                  label='Risk Level'
                >
                  <MenuItem value='low'>Low</MenuItem>
                  <MenuItem value='medium'>Medium</MenuItem>
                  <MenuItem value='high'>High</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label='Clinical Notes'
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label='Recommendations'
                value={formData.recommendations}
                onChange={(e) =>
                  setFormData({ ...formData, recommendations: e.target.value })
                }
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type='date'
                label='Follow-up Date'
                value={formData.followUpDate}
                onChange={(e) =>
                  setFormData({ ...formData, followUpDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant='contained' disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MentalHealthAssessment;
